import React from 'react';
import { Autocomplete, Box, Grid, InputLabel, OutlinedInput, Typography } from '@mui/material';
import DefaultSelectClient from '../../../../components/default-select-client';
import { DateField, FieldError, FormSectionTitle } from '../../../../components/form-page-parts';
import { ValidationErrors } from '../../../../utils/validation';
import { LookupSelect, toLookupOptions } from '../../shared/lookup-select';
import { useContractCreateEditState } from '../context/contract-create-edit-context';
import { ContractField } from '../context/contract-validation';
import { IContractAddEditRequest } from '../../../../model/scp/contract/request/contract-add-edit-request';
import { IPurchasePagedResponse } from '../../../../model/scp/purchase/response/purchase-paged-response';

function matchesPurchase(option: IPurchasePagedResponse, term: string): boolean {
    return [option.number, option.numberProcess, option.year, option.modality, option.disputeMode]
        .some((value) => value?.toString().toLowerCase().includes(term));
}

interface ContractIdentificationSectionProps {
    errors: ValidationErrors<ContractField>;
    onChange: (e: any) => void;
    onDateChange: (field: keyof IContractAddEditRequest, value: string | null) => void;
}

// Cliente, identificação, compra vinculada, classificação e vigência do contrato
export function ContractIdentificationSection({ errors, onChange, onDateChange }: ContractIdentificationSectionProps) {
    const { contractRequest, purchases, unidEntity, typeContract, categoryProcess } = useContractCreateEditState();
    const unitOptions = unidEntity.map((unit) => ({ value: unit.unitCode, label: `${unit.unitCode} - ${unit.name}` }));

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <FormSectionTitle title="Cliente" first />
                <DefaultSelectClient required clientId={contractRequest.clientId} handleOnChange={onChange} error={errors.clientId || false} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Identificação do Contrato" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
                <InputLabel required>Nº Empenho do Contrato</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" name="input-number-contract-effort" value={contractRequest.contractEffortNumber || ''} onChange={onChange} error={!!errors.contractEffortNumber} />
                <FieldError message={errors.contractEffortNumber} />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
                <InputLabel required>Nº Processo</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" name="input-process-number" value={contractRequest.processNumber || ''} onChange={onChange} error={!!errors.processNumber} />
                <FieldError message={errors.processNumber} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
                <InputLabel required>Exercício</InputLabel>
                <OutlinedInput fullWidth autoComplete="off" type="number" name="input-year-contract" value={contractRequest.yearContract || ''} onChange={onChange} error={!!errors.yearContract} />
                <FieldError message={errors.yearContract} />
            </Grid>
            <Grid item xs={12} md={4} lg={6}>
                <InputLabel required>Compra ou Licitação</InputLabel>
                <Autocomplete
                    fullWidth
                    options={purchases}
                    value={purchases.find((p) => p.id === Number(contractRequest.purchaseId)) || null}
                    onChange={(_event, newValue) => onChange({ target: { name: 'select-purchase', value: newValue?.id } })}
                    getOptionLabel={(option) => `Nº ${option.number} | Processo: ${option.numberProcess} | Ano: ${option.year}`}
                    filterOptions={(options, { inputValue }) => {
                        const term = inputValue.toLowerCase().trim();
                        return term ? options.filter((option) => matchesPurchase(option, term)) : options;
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                            <Typography variant="body2" fontWeight={600}>Nº {option.number} - Processo: {option.numberProcess}</Typography>
                            <Typography variant="caption" color="text.secondary">Exercício: {option.year} | Modalidade: {option.modality} | Modo de disputa: {option.disputeMode}</Typography>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <OutlinedInput
                            fullWidth
                            ref={params.InputProps.ref}
                            className={params.InputProps.className}
                            endAdornment={params.InputProps.endAdornment}
                            inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                            placeholder="Digite para buscar..."
                            error={!!errors.purchaseId}
                        />
                    )}
                    noOptionsText="Nenhuma compra encontrada"
                />
                <FieldError message={errors.purchaseId} />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Classificação" />
            </Grid>
            <Grid item xs={12} sm={5}>
                <LookupSelect required label="Unidade" name="select-unid-entity" value={contractRequest.codBuyerUnit} options={unitOptions} onChange={onChange} error={errors.codBuyerUnit} emptyLabel="Selecionar" />
            </Grid>
            <Grid item xs={12} sm={4}>
                <LookupSelect required label="Tipo de Contrato" name="select-contract-type" value={contractRequest.contractTypeId} options={toLookupOptions(typeContract)} onChange={onChange} error={errors.contractTypeId} emptyLabel="Selecionar" />
            </Grid>
            <Grid item xs={12} sm={3}>
                <LookupSelect required label="Categoria" name="select-cateogry-process" value={contractRequest.categoryProcessId} options={toLookupOptions(categoryProcess)} onChange={onChange} error={errors.categoryProcessId} emptyLabel="Selecionar" />
            </Grid>

            <Grid item xs={12}>
                <FormSectionTitle title="Datas e Vigência" />
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
                <DateField required label="Data de Assinatura" value={contractRequest.signDate} onChange={(value) => onDateChange('signDate', value)} error={errors.signDate} />
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
                <DateField required label="Início de Vigência" value={contractRequest.effectiveDateStart} onChange={(value) => onDateChange('effectiveDateStart', value)} error={errors.effectiveDateStart} />
            </Grid>
            <Grid item xs={12} sm={4} lg={3}>
                <DateField required label="Fim de Vigência" value={contractRequest.effectiveDateEnd} onChange={(value) => onDateChange('effectiveDateEnd', value)} error={errors.effectiveDateEnd} />
            </Grid>
        </React.Fragment>
    );
}
